import { InternalError } from "@gadgetinc/errors";
import type { AnyVariables, Client, Exchange, TypedDocumentNode } from "@urql/core";
import { isFunction, memoize } from "lodash";
import { stringify } from "qs";
import { cacheExchange, createClient, fetchExchange } from "urql";
import { onEnd, pipe, subscribe, tap } from "wonka";
import type { EnvironmentSlug, LegacyEnvironmentName } from "./Environment";
import { graphqlDocumentName } from "./graphqlUtils";

export const urlParamExchange: Exchange = ({ forward }) => {
  return (ops$) =>
    pipe(
      ops$,
      tap((op) => {
        if (op.context.url && op.query && !op.context.url.includes("?")) {
          const params = {
            operation: graphqlDocumentName(op.query) || "unknown",
          };

          op.context = {
            ...op.context,
            url: op.context.url + `?` + stringify(params),
          };
        }
      }),
      forward
    );
};

/**
 * Tags all outgoing query operations as from the editor with the x-gadget-client header, so we can suppress logging in the backend. Mutations are left untouched so they show up clearly in logs.
 **/
export const markAsEditorExchange: Exchange = ({ forward }) => {
  return (ops$) =>
    pipe(
      ops$,
      tap((op) => {
        if (isFunction(op.context.fetchOptions)) {
          throw new InternalError("Can't set editor details on an options with fetch options defined as a function");
        }
        op.context.fetchOptions ??= {};
        op.context.fetchOptions.headers ??= {};
        (op.context.fetchOptions.headers as any)["x-gadget-client"] ??= "editor";
        if (op.kind == "query") {
          (op.context.fetchOptions.headers as any)["x-log-level"] ??= "20"; // UserspaceLogLevel.DEBUG
        }
      }),
      forward
    );
};

/**
 * Tags all outgoing query operations as from the editor with the x-gadget-environment header which tells the backend which env to process the request against.
 * In the editor, we always send requests to the main production domain which has the user's auth cookie on it. We need this header to indicate what the real env to use is.
 **/
export const currentEnvironmentExchange = (currentEnvironment: LegacyEnvironmentName | EnvironmentSlug): Exchange => {
  return ({ forward }) => {
    return (ops$) =>
      pipe(
        ops$,
        tap((op) => {
          if (isFunction(op.context.fetchOptions)) {
            throw new InternalError("Can't set environment on an options with fetch options defined as a function");
          }

          op.context.fetchOptions ??= {};
          op.context.fetchOptions.headers ??= {};
          (op.context.fetchOptions.headers as any)["x-gadget-environment"] ??= currentEnvironment;
        }),
        forward
      );
  };
};

/**
 * Tags subscription operations with an extension to mark them as completed on the last message when they are complete
 */
export const teardownExchange: Exchange = ({ forward }) => {
  return (ops$) =>
    pipe(
      ops$,
      tap((op) => {
        if (op.kind === "teardown") {
          op.context.torndown = true;
        }
      }),
      forward
    );
};

export const getAppUrqlClient = memoize((environment: LegacyEnvironmentName | EnvironmentSlug) =>
  createClient({
    url: "/api/graphql",
    exchanges: [currentEnvironmentExchange(environment), cacheExchange, urlParamExchange, markAsEditorExchange, fetchExchange],
  })
);

export function subscriptionToGenerator<T, V extends AnyVariables>(
  client: Client,
  query: TypedDocumentNode<T, V>,
  variables: V
): [AsyncGenerator<T, void, unknown>, () => void] {
  const subscription = client.subscription<T, V>(query, variables);
  let unsubscribeFn: (() => void) | undefined;
  const queue: { data?: T; error?: Error; complete?: true }[] = [];

  const generator = (async function* () {
    let resolveNext: (() => void) | null = null;

    const waitForNext = (): Promise<void> =>
      queue.length > 0
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            resolveNext = resolve;
          });

    const subscriptionObserver = pipe(
      subscription,
      onEnd(() => {
        queue.push({ complete: true });
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }
      }),
      subscribe((result) => {
        if (result.error) {
          queue.push({ error: result.error });
        } else {
          queue.push({ data: result.data });
        }
        if (resolveNext) {
          resolveNext();
          resolveNext = null;
        }
      })
    );
    unsubscribeFn = subscriptionObserver.unsubscribe;

    try {
      while (true) {
        await waitForNext();
        const event = queue.shift();
        if (!event) continue;
        if (event.error) {
          throw event.error;
        }
        if (event.complete) {
          break;
        }
        if (event.data !== undefined) {
          yield event.data;
        }
      }
    } finally {
      unsubscribeFn?.();
    }
  })();

  const cleanup = () => {
    queue.push({ complete: true });
    unsubscribeFn?.();
  };

  return [generator, cleanup];
}
